import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { WorkflowList } from './components/WorkflowList';
import { WorkflowEditor } from './components/WorkflowEditor';
import { BlockLibrary } from './components/BlockLibrary';
import { Navigation } from './components/Navigation';
import { SettingsModal } from './components/SettingsModal';
import { Workflow, Block, Agent } from './types';
import { api } from './api';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import Agents from './pages/Agents';
import DatabaseViewer from './pages/DatabaseViewer';
import NetworkMonitor from './components/NetworkMonitor';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import WorkflowDebugger from './pages/WorkflowDebugger';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';

// Protected Route component to redirect unauthorized users
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // Redirect to login with return path
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
};

// Admin Route component to restrict access to admin users
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  
  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // Redirect to login with return path
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (user?.role !== 'admin') {
    // Redirect to home if not an admin
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// Component to track location changes and reload workflows
const LocationChangeHandler: React.FC<{ onLocationChange: (pathname: string) => void }> = ({ onLocationChange }) => {
  const location = useLocation();
  
  React.useEffect(() => {
    onLocationChange(location.pathname);
  }, [location.pathname, onLocationChange]);
  
  return null;
};

// Inner component that has access to useAuth()
const AppContent: React.FC = () => {
  const [workflows, setWorkflows] = React.useState<Workflow[]>([]);
  const [blocks, setBlocks] = React.useState<Block[]>([]);
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Load workflows and blocks only after authentication is complete
  React.useEffect(() => {
    // Don't load data until authentication is ready
    if (authLoading) {
      console.log('App - waiting for authentication...');
      return;
    }

    // Only load data if authenticated
    if (!isAuthenticated) {
      console.log('App - not authenticated, skipping data load');
      return;
    }

    console.log('App - authentication ready, loading workflows and blocks');
    loadWorkflows();
    loadBlocks();
  }, [authLoading, isAuthenticated]);

  // Verify API key on application startup (only when authenticated)
  React.useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    const verifyApiKey = async () => {
      try {
        const keyStatus = await api.getOpenAIKeyStatus();
        console.log('App startup - API key status:', keyStatus.keyStatus === 'configured' ? 'present' : 'missing');
      } catch (error) {
        console.error('Error checking API key on startup:', error);
      }
    };
    
    verifyApiKey();
  }, [authLoading, isAuthenticated]);

  // Handle location changes to reload workflows when navigating to workflows page
  const handleLocationChange = React.useCallback((pathname: string) => {
    if (pathname === '/workflows') {
      console.log('Navigated to workflows page, reloading workflows...');
      loadWorkflows();
    }
  }, []);

  const loadWorkflows = async () => {
    try {
      const data = await api.getWorkflows();
      setWorkflows(data || []);
    } catch (err) {
      console.error('Failed to load workflows:', err);
      setWorkflows([]);
    }
  };

  const loadBlocks = async () => {
    try {
      const data = await api.getBlocks();
      setBlocks(data || []);
    } catch (err) {
      console.error('Failed to load blocks:', err);
    }
  };

  const handleCreateWorkflow = async (name: string) => {
    try {
      const workflow = await api.createWorkflow({
        name,
        description: '',
        blocks: [],
        connections: [],
      });
      setWorkflows(prev => [...prev, workflow]);
      return workflow;
    } catch (err) {
      console.error('Failed to create workflow:', err);
      throw err;
    }
  };

  const handleCreateBlock = async (block: Omit<Block, 'id'>) => {
    try {
      const newBlock = await api.createBlock(block);
      setBlocks(prev => [...prev, newBlock]);
      return newBlock;
    } catch (err) {
      console.error('Failed to create block:', err);
      throw err;
    }
  };

  const handleUpdateBlock = async (id: string, block: Block) => {
    try {
      const updatedBlock = await api.updateBlock(id, block);
      setBlocks(prev => prev.map(b => b.id === id ? updatedBlock : b));
      return updatedBlock;
    } catch (err) {
      console.error('Failed to update block:', err);
      throw err;
    }
  };

  const handleDeleteBlock = async (id: string) => {
    try {
      await api.deleteBlock(id);
      setBlocks(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      console.error('Failed to delete block:', err);
      throw err;
    }
  };

  const handleDeleteWorkflow = async (id: string) => {
    try {
      await api.deleteWorkflow(id);
      setWorkflows(prev => prev.filter(w => w.id !== id));
    } catch (err) {
      console.error('Failed to delete workflow:', err);
      throw err;
    }
  };

  const getWorkflowAgents = async (workflowId: string): Promise<Agent[]> => {
    try {
      const agents = await api.getAgents();
      // Ensure agents is an array before filtering
      return agents ? agents.filter(agent => agent.workflowId === workflowId) : [];
    } catch (err) {
      console.error('Failed to get workflow agents:', err);
      return [];
    }
  };

  const openSettings = () => {
    setIsSettingsOpen(true);
  };

  const closeSettings = () => {
    setIsSettingsOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <LocationChangeHandler onLocationChange={handleLocationChange} />
        <NetworkMonitor />
        <ToastContainer />
        <div className="min-h-screen flex flex-col bg-gray-100">
          <Navigation onOpenSettings={openSettings} />
          <main className="flex-grow">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Protected routes */}
              <Route path="/" element={<ProtectedRoute><Navigate to="/workflows" replace /></ProtectedRoute>} />
              
              <Route path="/blocks" element={
                <ProtectedRoute>
                  <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 w-full">
                    <BlockLibrary
                      blocks={blocks}
                      onCreateBlock={handleCreateBlock}
                      onUpdateBlock={handleUpdateBlock}
                      onDeleteBlock={handleDeleteBlock}
                    />
                  </div>
                </ProtectedRoute>
              } />
              
              <Route path="/workflows" element={
                <ProtectedRoute>
                  <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 w-full">
                    <WorkflowList
                      workflows={workflows}
                      onCreateWorkflow={handleCreateWorkflow}
                      onDeleteWorkflow={handleDeleteWorkflow}
                      getWorkflowAgents={getWorkflowAgents}
                    />
                  </div>
                </ProtectedRoute>
              } />
              
              <Route path="/workflows/:workflowId" element={
                <ProtectedRoute>
                  <WorkflowEditor />
                </ProtectedRoute>
              } />
              
              <Route path="/agents" element={
                <ProtectedRoute>
                  <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 w-full">
                    <Agents />
                  </div>
                </ProtectedRoute>
              } />

              <Route path="/profile" element={
                <ProtectedRoute>
                  <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 w-full">
                    <Profile />
                  </div>
                </ProtectedRoute>
              } />

              <Route path="/admin" element={
                <AdminRoute>
                  <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 w-full">
                    <Admin onOpenSettings={openSettings} />
                  </div>
                </AdminRoute>
              } />

              <Route path="/admin/database" element={
                <AdminRoute>
                  <DatabaseViewer />
                </AdminRoute>
              } />

              <Route path="/debug-workflows" element={
                <ProtectedRoute>
                  <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 w-full">
                    <WorkflowDebugger />
                  </div>
                </ProtectedRoute>
              } />
            </Routes>
          </main>
        </div>
        
        {isSettingsOpen && (
          <SettingsModal isOpen={isSettingsOpen} onClose={closeSettings} />
        )}
      </Router>
    </ThemeProvider>
  );
};

// Main App component that wraps everything with AuthProvider
export const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};
